import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Lock, Play, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  fmtMoney2,
  fmtRate,
  monthLabel,
  previousMonth,
  recentMonths,
  useLockPeriod,
  usePeriodReport,
  useRunPeriod,
  type PayeeGroup,
  type PayoutLine,
} from "../api";

export function ReportView() {
  const [period, setPeriod] = useState(previousMonth());
  const { data, error, isLoading } = usePeriodReport(period);
  const run = useRunPeriod(period);
  const lock = useLockPeriod(period);

  const locked = data?.status === "locked";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Payout month
          </span>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue>{monthLabel(period)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {recentMonths(24).map((m) => (
                <SelectItem key={m} value={m}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {locked && (
            <Badge variant="outline" className="gap-1">
              <Lock className="h-3 w-3" />
              Locked
              {data?.locked_by_name ? ` by ${data.locked_by_name}` : ""}
            </Badge>
          )}
          {data?.exists && !locked && <Badge variant="secondary">Draft</Badge>}
        </div>

        {data?.can_admin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={locked || run.isPending}
              onClick={() =>
                run.mutate(undefined, {
                  onSuccess: () => toast.success(`${monthLabel(period)} computed`),
                  onError: (e) =>
                    toast.error("Run failed", { description: (e as Error).message }),
                })
              }
            >
              {run.isPending ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Play className="mr-1.5 h-4 w-4" />
              )}
              {run.isPending ? "Computing…" : data?.exists ? "Recompute" : "Run month"}
            </Button>
            <Button
              size="sm"
              disabled={!data?.exists || locked || lock.isPending || run.isPending}
              onClick={() =>
                lock.mutate(undefined, {
                  onSuccess: () => toast.success(`${monthLabel(period)} locked`),
                  onError: (e) =>
                    toast.error("Couldn't lock", { description: (e as Error).message }),
                })
              }
            >
              <Lock className="mr-1.5 h-4 w-4" />
              {lock.isPending ? "Locking…" : "Lock month"}
            </Button>
          </div>
        )}
      </div>

      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the report</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {isLoading && !data && <Card className="h-40 animate-pulse" />}

      {data && !data.exists && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <Clock className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">{monthLabel(period)} hasn't been run yet</p>
          <p className="max-w-md text-sm text-muted-foreground">
            {data.can_admin
              ? "Run the month to compute payouts from the paid-invoice snapshot."
              : "Once the month is run and reconciled (R-12), your payout lines appear here."}
          </p>
        </Card>
      )}

      {data && data.exists && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <SummaryTile
              label="Payable this month"
              value={data.grand_payable}
              tone="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <SummaryTile
              label="Catch-ups (late payments)"
              value={data.grand_catchup}
              tone="info"
              icon={<RefreshCw className="h-4 w-4" />}
            />
            <SummaryTile
              label="Held — invoice unpaid"
              value={data.grand_held}
              tone="warning"
              icon={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          {data.groups.length === 0 && (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No commission lines for you this month.
            </Card>
          )}

          {data.groups.length > 0 && <PayoutSummaryTable groups={data.groups} />}
        </>
      )}
    </div>
  );
}

/** The "Total Payout Amounts" view: one expandable row per payee, expanding
 *  to the per-company breakdown with line detail and company subtotals. */
function PayoutSummaryTable({ groups }: { groups: PayeeGroup[] }) {
  const columns: DataTableColumn<PayeeGroup>[] = [
    {
      key: "payee_name",
      header: "Payee",
      render: (g) => <span className="font-medium">{g.payee_name}</span>,
    },
    {
      key: "companies",
      header: "Companies",
      numeric: true,
      render: (g) => new Set(g.lines.map((l) => l.company_name)).size,
    },
    { key: "lines", header: "Lines", numeric: true, render: (g) => g.lines.length },
    {
      key: "payable_total",
      header: "Payable",
      numeric: true,
      render: (g) => fmtMoney2(g.payable_total),
    },
    {
      key: "catchup_total",
      header: "Catch-up",
      numeric: true,
      render: (g) => (g.catchup_total !== 0 ? fmtMoney2(g.catchup_total) : "—"),
    },
    {
      key: "held_total",
      header: "Held",
      numeric: true,
      render: (g) =>
        g.held_total !== 0 ? (
          <span className="text-warning">{fmtMoney2(g.held_total)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "total",
      header: "Total payout",
      numeric: true,
      render: (g) => (
        <span className="font-semibold">
          {fmtMoney2(g.payable_total + g.catchup_total)}
        </span>
      ),
    },
  ];

  const grand = {
    payable: groups.reduce((s, g) => s + g.payable_total, 0),
    catchup: groups.reduce((s, g) => s + g.catchup_total, 0),
    held: groups.reduce((s, g) => s + g.held_total, 0),
  };

  return (
    <DataTable
      columns={columns}
      rows={groups}
      rowKey={(g) => g.payee_id}
      renderExpanded={(g) => <PayeeCompanyBreakdown group={g} />}
      containerClassName=""
      footer={() => (
        <TableRow className="font-semibold">
          <TableCell /> {/* chevron column */}
          <TableCell>Total payout amounts</TableCell>
          <TableCell />
          <TableCell />
          <TableCell numeric>{fmtMoney2(grand.payable)}</TableCell>
          <TableCell numeric>{grand.catchup !== 0 ? fmtMoney2(grand.catchup) : "—"}</TableCell>
          <TableCell numeric className="text-warning">
            {grand.held !== 0 ? fmtMoney2(grand.held) : "—"}
          </TableCell>
          <TableCell numeric>{fmtMoney2(grand.payable + grand.catchup)}</TableCell>
        </TableRow>
      )}
    />
  );
}

/** Expanded detail: the payee's lines grouped by company, with a subtotal row
 *  per company — "how much commission per company", plus the line evidence. */
function PayeeCompanyBreakdown({ group }: { group: PayeeGroup }) {
  const byCompany = new Map<string, PayoutLine[]>();
  for (const line of group.lines) {
    const key = line.company_name ?? "—";
    const list = byCompany.get(key) ?? [];
    list.push(line);
    byCompany.set(key, list);
  }
  const companies = [...byCompany.entries()].sort((a, b) => {
    const total = (lines: PayoutLine[]) => lines.reduce((s, l) => s + l.amount, 0);
    return total(b[1]) - total(a[1]);
  });

  return (
    <div className="overflow-x-auto">
      <Table containerClassName="max-h-[420px]">
        <TableHeader>
          <TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Item</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead>Month</TableHead>
            <TableHead align="right">Base</TableHead>
            <TableHead align="right">Rate</TableHead>
            <TableHead align="right">Commission</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.map(([company, lines]) => {
            const subtotal = lines.reduce((s, l) => s + l.amount, 0);
            return (
              <React.Fragment key={company}>
                {lines.map((line, i) => (
                  <TableRow
                    key={line.id}
                    className={cn(line.status === "held_unpaid_invoice" && "opacity-70")}
                  >
                    <TableCell
                      className="max-w-[220px] truncate font-medium"
                      title={company}
                    >
                      {i === 0 ? company : ""}
                    </TableCell>
                    <TableCell className="max-w-[260px] truncate" title={line.item_name ?? ""}>
                      {line.item_name ?? "—"}
                      {line.notes && (
                        <span className="ml-1.5 text-xs text-warning" title={line.notes}>
                          ⚠
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {line.invoice_document_id ?? "—"}
                    </TableCell>
                    <TableCell>{line.month_index ?? "—"}</TableCell>
                    <TableCell numeric className="tabular-nums">
                      {fmtMoney2(line.base_amount)}
                    </TableCell>
                    <TableCell numeric className="tabular-nums">
                      {fmtRate(line.rate_pct)}
                    </TableCell>
                    <TableCell
                      numeric
                      className={cn("tabular-nums", line.amount < 0 && "text-danger")}
                    >
                      {fmtMoney2(line.amount)}
                    </TableCell>
                    <TableCell>
                      <LineStatusBadge line={line} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableCell className="text-xs font-medium text-muted-foreground">
                    {company} subtotal
                  </TableCell>
                  <TableCell colSpan={5} />
                  <TableCell numeric className="font-semibold tabular-nums">
                    {fmtMoney2(subtotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function SummaryTile({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number;
  tone: "success" | "info" | "warning";
  icon: React.ReactNode;
}) {
  return (
    <Card className="flex items-center justify-between p-4">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{fmtMoney2(value)}</p>
      </div>
      <div
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-full",
          tone === "success" && "bg-success-light text-success",
          tone === "info" && "bg-info-light text-info",
          tone === "warning" && "bg-warning-light text-warning",
        )}
      >
        {icon}
      </div>
    </Card>
  );
}

function LineStatusBadge({ line }: { line: PayoutLine }) {
  if (line.status === "payable" || line.status === "imported") {
    return <Badge variant="success">Payable</Badge>;
  }
  if (line.status === "catchup") {
    return (
      <Badge variant="info">
        Catch-up{line.held_from_period ? ` · covers ${monthLabel(line.held_from_period)}` : ""}
      </Badge>
    );
  }
  return <Badge variant="warning">Held — unpaid</Badge>;
}
